import React from 'react';
import { ArrowUpRight, ArrowDownRight, DollarSign, FileText, Activity, AlertCircle } from 'lucide-react';
import { StatusBadge } from '../components/StatusBadge';
import { Page } from '../components/Layout';
interface DashboardProps {
  onNavigate: (page: Page) => void;
}
export function Dashboard({
  onNavigate
}: DashboardProps) {
  const metrics = [{
    title: 'Total Disputed (YTD)',
    value: '$1,452,100',
    change: '+12.5%',
    trend: 'up',
    icon: AlertCircle,
    color: 'text-blue-600',
    bg: 'bg-blue-100'
  }, {
    title: 'Recovered Cash',
    value: '$528,400',
    change: '56% Win Rate',
    trend: 'up',
    icon: DollarSign,
    color: 'text-emerald-600',
    bg: 'bg-emerald-100'
  }, {
    title: 'Pending',
    value: '$310,000',
    change: '45 Active Claims',
    trend: 'neutral',
    icon: Activity,
    color: 'text-amber-600',
    bg: 'bg-amber-100'
  }, {
    title: 'Processing Volume',
    value: '240',
    change: 'Claims this month',
    trend: 'up',
    icon: FileText,
    color: 'text-indigo-600',
    bg: 'bg-indigo-100'
  }];
  const batches = [{
    id: 1,
    retailer: 'Dollar General',
    check: '#DG-9982',
    date: 'Feb 18, 2026',
    status: 'not-started',
    deduction: '$48,200',
    rejected: 5
  }, {
    id: 2,
    retailer: 'Dollar General',
    check: '#DG-8821',
    date: 'Feb 18, 2026',
    status: 'in-progress',
    deduction: '$12,500',
    rejected: 40
  }, {
    id: 3,
    retailer: 'Walmart',
    check: '#WM-2219',
    date: 'Feb 17, 2026',
    status: 'in-progress',
    deduction: '$31,800',
    rejected: 35
  }, {
    id: 4,
    retailer: 'Dollar General',
    check: '#DG-1102',
    date: 'Feb 2, 2026',
    status: 'completed',
    deduction: '$8,900',
    rejected: 8
  }];
  return <div className="space-y-8" data-id="element-292">
      <div data-id="element-293">
        <h2 className="text-2xl font-bold text-slate-900 tracking-tight" data-id="element-294">
          Dashboard
        </h2>
        <p className="text-slate-500 mt-1" data-id="element-295">
          Overview of your audit recovery performance.
        </p>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-296">
        {metrics.map(metric => <div key={metric.title} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow duration-200" data-id="element-297">
            <div className="flex items-center justify-between mb-4" data-id="element-298">
              <div className={`p-2 rounded-lg ${metric.bg}`} data-id="element-299">
                <metric.icon className={`w-5 h-5 ${metric.color}`} data-id="element-300" />
              </div>
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${metric.trend === 'up' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-301">
                {metric.change}
              </span>
            </div>
            <h3 className="text-slate-500 text-sm font-medium" data-id="element-302">
              {metric.title}
            </h3>
            <p className="text-2xl font-bold text-slate-900 mt-1" data-id="element-303">
              {metric.value}
            </p>
          </div>)}
      </div>

      {/* Win Rate By Retailer */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-304">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-305">
          <div className="p-6 border-b border-slate-100 flex justify-between items-center" data-id="element-306">
            <h3 className="font-semibold text-slate-900" data-id="element-307">
              Recent Batches (Checks)
            </h3>
            <button onClick={() => onNavigate('checks')} className="text-sm text-blue-600 font-medium hover:text-blue-700" data-id="element-308">
              View All
            </button>
          </div>
          <div className="overflow-x-auto" data-id="element-309">
            <table className="w-full text-sm text-left" data-id="element-310">
              <thead className="bg-slate-50 text-slate-500 font-medium" data-id="element-311">
                <tr data-id="element-312">
                  <th className="px-6 py-3" data-id="element-313">Retailer / Check #</th>
                  <th className="px-6 py-3" data-id="element-314">Date Received</th>
                  <th className="px-3 py-3 w-20" data-id="element-315">Status</th>
                  <th className="px-6 py-3" data-id="element-316">Total Claims</th>
                  <th className="px-6 py-3" data-id="element-317">Deduction Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-318">
                {batches.map(batch => <tr key={batch.id} className="hover:bg-slate-50 transition-colors" data-id="element-319">
                    <td className="px-6 py-4" data-id="element-320">
                      <div className="font-medium text-slate-900" data-id="element-321">
                        {batch.retailer}
                      </div>
                      <div className="text-[11px] text-slate-500 whitespace-nowrap" data-id="element-322">
                        {batch.check}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600" data-id="element-323">{batch.date}</td>
                    <td className="px-3 py-4 w-20" data-id="element-324">
                      <StatusBadge status={batch.status === 'completed' ? 'success' : batch.status === 'in-progress' ? 'processing' : batch.status === 'not-started' ? 'neutral' : batch.status === 'finance-review' ? 'Human Review' : batch.status === 'review' ? 'warning' : 'error'} className="text-[10px]" tooltip={batch.status === 'not-started' ? 'Waiting on the Logistics Team to confirm which deductions are invalid' : batch.status === 'in-progress' ? 'Sent emails to the retailer, waiting for responses' : batch.status === 'completed' ? 'Sent emails to the retailer and received responses. Click for details on how many approved or rejected.' : undefined} data-id="element-325">
                        {batch.status === 'not-started' ? 'Logistics Review' : batch.status === 'in-progress' ? 'Processing' : batch.status === 'finance-review' ? 'Finance Review' : batch.status.charAt(0).toUpperCase() + batch.status.slice(1)}
                      </StatusBadge>
                    </td>
                    <td className="px-6 py-4 font-semibold text-slate-900" data-id="element-326">
                      {batch.rejected}
                    </td>
                    <td className="px-6 py-4 font-medium text-slate-900" data-id="element-327">
                      {batch.deduction}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-id="element-328">
          <h3 className="font-semibold text-slate-900 mb-6" data-id="element-329">
            Win Rate by Retailer
          </h3>
          <div className="space-y-6" data-id="element-330">
            {[{
            name: 'Dollar General',
            rate: 65,
            color: 'bg-yellow-500'
          }, {
            name: 'Walmart',
            rate: 60,
            color: 'bg-blue-500'
          }, {
            name: 'Dollar General',
            rate: 58,
            color: 'bg-yellow-400'
          }, {
            name: 'Whole Foods',
            rate: 57,
            color: 'bg-green-500'
          }, {
            name: 'Amazon',
            rate: 45,
            color: 'bg-orange-500'
          }, {
            name: 'Target',
            rate: 35,
            color: 'bg-red-500'
          }].map((retailer, index) => <div key={`${retailer.name}-${index}`} data-id="element-331">
                <div className="flex justify-between text-sm mb-2" data-id="element-332">
                  <span className="font-medium text-slate-700" data-id="element-333">
                    {retailer.name}
                  </span>
                  <span className="font-bold text-slate-900" data-id="element-334">
                    {retailer.rate}%
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-335">
                  <div className={`${retailer.color} h-2 rounded-full transition-all duration-1000 ease-out`} style={{
                width: `${retailer.rate}%`
              }} data-id="element-336"></div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}